import { Expense } from '../types';

/** Canonical Hebrew labels for expense fields — single source of truth for UI copy. */
export const FIELD_LABELS = {
  vendorName: 'שם ספק',
  amount: 'סכום נטו (לפני מע״מ)',
  amountShort: 'סכום נטו',
  vatAmount: 'סכום מע״מ',
  vatAmountShort: 'מע״מ',
  totalAmount: 'סכום כולל (כולל מע״מ)',
  totalAmountShort: 'סכום כולל',
  documentDate: 'תאריך חשבונית',
  documentDateShort: 'תאריך\nחשבונית',
  creationTimestamp: 'תאריך העלאה',
  creationTimestampShort: 'תאריך\nהעלאה',
  submittedBy: 'הוגש על ידי',
  submittedByShort: 'הוגש על ידי',
  updatedBy: 'שונה לאחרונה על ידי',
  invoiceNumber: 'מספר חשבונית',
  invoiceNumberShort: 'מס חשבונית',
  documentNumber: 'מספר חשבונית',
  category1: 'קטגוריה ראשית',
  category1Short: 'קטגוריה L1',
  category2: 'תת־קטגוריה',
  category2Short: "תת קט' L2",
  category3: 'פירוט קטגוריה',
  category3Short: 'פירוט L3',
  projectTag: 'תג פרויקט',
  projectTagShort: 'תג',
  notes: 'הערות',
  currency: 'מטבע',
  originalAmount: 'סכום',
  fxRate: 'שער',
  serialNumber: 'מספר סידורי',
  lineId: 'מס׳',
  credit: 'זיכוי',
} as const;

export type TrackedField =
  | 'vendorName'
  | 'amount'
  | 'documentDate'
  | 'category1'
  | 'invoiceNumber';

/** Fields that commonly need completion after OCR / upload. */
export const COMPLETENESS_FIELDS: {
  key: TrackedField;
  label: string;
  getValue: (expense: Expense) => string | number | undefined | null;
  isEmpty: (value: string | number | undefined | null) => boolean;
}[] = [
  {
    key: 'vendorName',
    label: FIELD_LABELS.vendorName,
    getValue: (e) => e.vendorName,
    isEmpty: (v) => !String(v ?? '').trim(),
  },
  {
    key: 'amount',
    label: FIELD_LABELS.amountShort,
    getValue: (e) => e.amount,
    isEmpty: (v) => v === undefined || v === null || v === '' || Number(v) === 0,
  },
  {
    key: 'documentDate',
    label: FIELD_LABELS.documentDate,
    getValue: (e) => e.documentDate,
    isEmpty: (v) => !String(v ?? '').trim(),
  },
  {
    key: 'category1',
    label: FIELD_LABELS.category1,
    getValue: (e) => e.category1,
    isEmpty: (v) => !String(v ?? '').trim(),
  },
  {
    key: 'invoiceNumber',
    label: FIELD_LABELS.invoiceNumber,
    getValue: (e) => e.invoiceNumber || e.documentNumber,
    isEmpty: (v) => !String(v ?? '').trim(),
  },
];

/** Status-dot fields that force a red dot — even after Save. */
export type StatusDotCriticalField = 'vendorName' | 'totalAmount' | 'documentDate' | 'category1';

/** Soft missing fields → pink when not yet reviewed (L2 only; L3 ignored). */
export type StatusDotSoftField = 'category2';

export type StatusDotField = StatusDotCriticalField | StatusDotSoftField;

const isBlankText = (v: string | number | undefined | null) => !String(v ?? '').trim();
const isBlankAmount = (v: string | number | undefined | null) =>
  v === undefined || v === null || v === '' || Number(v) === 0;

/** True when none of net / VAT / total has a usable value (amounts stay linked in the UI). */
const getEffectiveAmount = (e: Expense): string | number | undefined | null => {
  if (!isBlankAmount(e.totalAmount)) return e.totalAmount;
  if (!isBlankAmount(e.amount)) return e.amount;
  if (!isBlankAmount(e.vatAmount)) return e.vatAmount;
  return e.totalAmount;
};

/** Red-dot / save-block checklist: vendor, amount (any of the three), invoice date, category L1. */
export const STATUS_DOT_CRITICAL_FIELDS: {
  key: StatusDotCriticalField;
  label: string;
  getValue: (expense: Expense) => string | number | undefined | null;
  isEmpty: (value: string | number | undefined | null) => boolean;
}[] = [
  {
    key: 'vendorName',
    label: FIELD_LABELS.vendorName,
    getValue: (e) => e.vendorName,
    isEmpty: isBlankText,
  },
  {
    key: 'totalAmount',
    label: FIELD_LABELS.totalAmount,
    getValue: getEffectiveAmount,
    isEmpty: isBlankAmount,
  },
  {
    key: 'documentDate',
    label: FIELD_LABELS.documentDate,
    getValue: (e) => e.documentDate,
    isEmpty: isBlankText,
  },
  {
    key: 'category1',
    label: FIELD_LABELS.category1,
    getValue: (e) => e.category1,
    isEmpty: isBlankText,
  },
];

/** Pink-dot checklist when not reviewed: L2 missing. L3 is optional. */
export const STATUS_DOT_SOFT_FIELDS: {
  key: StatusDotSoftField;
  label: string;
  getValue: (expense: Expense) => string | number | undefined | null;
  isEmpty: (value: string | number | undefined | null) => boolean;
}[] = [
  {
    key: 'category2',
    label: FIELD_LABELS.category2,
    getValue: (e) => e.category2,
    isEmpty: isBlankText,
  },
];

/** @deprecated Prefer STATUS_DOT_CRITICAL_FIELDS / STATUS_DOT_SOFT_FIELDS */
export const STATUS_DOT_FIELDS = [...STATUS_DOT_CRITICAL_FIELDS, ...STATUS_DOT_SOFT_FIELDS];

export function getMissingFields(expense: Expense): { key: TrackedField; label: string }[] {
  return COMPLETENESS_FIELDS.filter((field) => field.isEmpty(field.getValue(expense))).map(
    ({ key, label }) => ({ key, label })
  );
}

export function getCriticalMissingFields(
  expense: Expense
): { key: StatusDotCriticalField; label: string }[] {
  return STATUS_DOT_CRITICAL_FIELDS.filter((field) => field.isEmpty(field.getValue(expense))).map(
    ({ key, label }) => ({ key, label })
  );
}

export function getSoftMissingFields(
  expense: Expense
): { key: StatusDotSoftField; label: string }[] {
  return STATUS_DOT_SOFT_FIELDS.filter((field) => field.isEmpty(field.getValue(expense))).map(
    ({ key, label }) => ({ key, label })
  );
}

export function getStatusDotMissingFields(
  expense: Expense
): { key: StatusDotField; label: string }[] {
  return [...getCriticalMissingFields(expense), ...getSoftMissingFields(expense)];
}

export function getInvoiceNumber(expense: Pick<Expense, 'invoiceNumber' | 'documentNumber'>): string {
  return (expense.invoiceNumber || expense.documentNumber || '').trim();
}
